import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, take } from 'rxjs';
import {
  DocumentGenerationApiService,
  DocumentGenerationStatus,
  GenerateDocumentResponse,
  GetDocumentGenerationStatusResponse,
} from 'src/app/modules/document-generation/services/document-generation-api.service';
import { DocumentGenerationActions } from 'src/app/modules/document-generation/store/actions';
import { selectStartAskingGenerateDocumentStatus } from 'src/app/modules/document-generation/store/selectors';
import { AppState } from '../store/state';
import {
  sendDocumentGenerationFailedNotification,
  sendDocumentGenerationSuccessNotification,
} from '../utils/doc-generation.utils';
import { isPresent } from '../utils/isPresent';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DocumentGenerationWorker {
  private interval: any;
  private documentGenerationTrackingId: string = '';
  private pollingFrequencySeconds: number = 10;
  private timeoutSeconds: number = 600;
  generateDocumentResponse: GenerateDocumentResponse | null = null;

  constructor(
    private store$: Store<AppState>,
    private documentGenerationApiService: DocumentGenerationApiService,
    private translocoService: TranslocoService,
  ) {
    this.store$
      .select(selectStartAskingGenerateDocumentStatus)
      .pipe(untilDestroyed(this), filter(isPresent), distinctUntilChanged())
      .subscribe((askingGenerateDocumentStatus) => {
        if (askingGenerateDocumentStatus === true) {
          this.startAskingUpdates();
        }
      });
  }

  init(generateDocumentResponse: GenerateDocumentResponse): void {
    this.generateDocumentResponse = generateDocumentResponse;
    this.documentGenerationTrackingId = generateDocumentResponse.documentGenerationTrackingId;
  }

  public startAskingUpdates(): void {
    this.addLogInfo();

    let start = new Date().getTime();

    this.interval = setInterval(() => {
      const now = new Date().getTime();
      let timeSpent = (now - start) / 1000;

      if (timeSpent > this.timeoutSeconds) {
        console.log(
          `askDocGeneratingUpdates stopped by timeout: ${this.documentGenerationTrackingId} timeSpent: ${timeSpent}s / ${this.timeoutSeconds}s`,
        );
        sendDocumentGenerationFailedNotification(
          this.store$,
          this.translocoService,
          this.documentGenerationTrackingId,
        );
        this.stopWorker(DocumentGenerationStatus.Failure); // |Timeout
        return;
      }

      this.documentGenerationApiService
        .getdocumentGenerationStatus$(this.documentGenerationTrackingId)
        .pipe(take(1))
        .subscribe((response) => {
          this.processStatusResponse(timeSpent, response);
        });
    }, this.pollingFrequencySeconds * 1000);
  }

  addLogInfo(): void {
    console.log(`Background worker is running`);
    console.log(`documentGenerationTrackingId: ${this.documentGenerationTrackingId}`);
    console.log(`pollingFrequencySeconds: ${this.pollingFrequencySeconds}`);
    console.log(`timeoutSeconds: ${this.timeoutSeconds}`);
  }

  processStatusResponse(timeSpent: number, response: GetDocumentGenerationStatusResponse): void {
    const generationStatus = response.status;

    switch (generationStatus) {
      case DocumentGenerationStatus.InProgress: {
        console.log(
          `askDocumentGeneration InProgress: ${this.documentGenerationTrackingId} timeSpent: ${timeSpent}s / ${this.timeoutSeconds}s`,
        );
        break;
      }
      case DocumentGenerationStatus.Completed: {
        console.log(
          `askDocumentGeneration Completed: ${this.documentGenerationTrackingId} timeSpent: ${timeSpent}s / ${this.timeoutSeconds}s`,
        );
        sendDocumentGenerationSuccessNotification(
          this.store$,
          this.translocoService,
          this.generateDocumentResponse!,
          response.documentUniqueId,
        );
        this.stopWorker(generationStatus);
        break;
      }
      case DocumentGenerationStatus.Failure: {
        console.log(
          `askDocumentGeneration Failure: ${this.documentGenerationTrackingId} timeSpent: ${timeSpent}s / ${this.timeoutSeconds}s`,
        );
        sendDocumentGenerationFailedNotification(
          this.store$,
          this.translocoService,
          response.documentGenerationTrackingId,
        );
        this.stopWorker(generationStatus);
        break;
      }
    }
  }

  stopWorker(reportStatus: DocumentGenerationStatus) {
    clearInterval(this.interval);

    switch (reportStatus) {
      case DocumentGenerationStatus.Completed: {
        this.store$.dispatch(
          DocumentGenerationActions.generateDocumentSuccess({
            generateDocumentResponse: this.generateDocumentResponse!,
          }),
        );
        break;
      }
      case DocumentGenerationStatus.Failure: {
        this.store$.dispatch(DocumentGenerationActions.generateDocumentFailure());
        break;
      }
    }
  }
}
